// @ts-strict-ignore
import type { Node } from 'react-flow-renderer';
import omit from 'lodash/omit';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';


export function removeHighlightForNodes(nodes: Node[]) {
  return nodes.map(el => {
    if (el.style == null) {
      return el;
    }

    return {
      ...el,
      style: {
        ...omit(el.style, ['border', 'borderStyle', 'borderRadius']),
      },
    };
  });
}

export function generateWorkflowOptions(workflows, overrides = {}) {
  return (workflows || []).reduce((workflowOpts, workflow) => {
    /**
     * We allow the fetching of the workflow list in journey.jsx to include archived workflows
     * so the data can be used to populate information for any nodes already using an archived
     * workflow. However, we don't want archived workflows showing up in the form to add/swap
     * out a workflow node.
     *
     * This block also will filter out any workflows which have 0 versions since these are not
     * valid options for the Add Node Menu.
     */
    if (!workflow.archived && workflow.version_count > 0) {
      workflowOpts.push({
        label: workflow?.name,
        value: {
          ...workflow,
          applicationToken: workflow.application_token,
          type: 'workflow',
          workflowType: workflow.type,
          ...overrides,
        },
      });
    }

    return workflowOpts;
  }, []);
}

export function applyOutcomeOptionsConfigs(edges, configsByEdge) {
  const edgesClone = cloneDeep(edges);
  configsByEdge.forEach(edge => {
    const edgeToUpdate = edgesClone.find(edgeObj => edgeObj.id === edge.id);
    const existingConfig = edgeToUpdate.data.config || {};

    edgeToUpdate.data.config = merge(existingConfig, omit(edge, ['id']));
  });

  return edgesClone;
}

export function onOutcomeOptionsConfirm(reactFlowInstance, configsByEdge) {
  const updatedEdges = applyOutcomeOptionsConfigs(
    reactFlowInstance.getEdges(),
    configsByEdge,
  );

  reactFlowInstance.setEdges(updatedEdges);
}

export const checkConfigAfterStartEdge = (
  nodes,
  edges,
  startEdge,
  validationObject,
  isEdgeAlreadyConfiguringInvestigation,
) => {
  const seenNodes = new Set();
  const targetNodes = nodes.filter(node => node.id === startEdge.target);
  const nextNodeEdges = [];

  targetNodes.forEach(targetNode => {
    if (targetNode.type === 'outcome') {
      return;
    }

    nextNodeEdges.push(...edges.filter(edge => edge.source === targetNode.id));
  });

  const stack = [...nextNodeEdges];
  while (
    stack.length
    && !(
      validationObject.isInvestigationAlreadyConfigured
      && validationObject.isCaseAlreadyConfigured
    )
  ) {
    const currentEdge = stack.pop();

    if (currentEdge.data.config?.opensInvestigation) {
      validationObject.isInvestigationAlreadyConfigured = true;
      validationObject.canConfigureInvestigationOnEdge = false;
      validationObject.canConfigureCaseOnEdge = isEdgeAlreadyConfiguringInvestigation || false;
    }

    if (currentEdge.data.config?.promotesToCase) {
      if (!validationObject.isCaseAlreadyConfigured) {
        validationObject.isCaseAlreadyConfigured = true;
        validationObject.canConfigureCaseOnEdge = false;
      }
    }

    if (
      validationObject.isCaseAlreadyConfigured
      && validationObject.isInvestigationAlreadyConfigured
    ) {
      break;
    }

    const targetNode = nodes.find(node => node.id === currentEdge.target);

    if (targetNode.data.type === 'outcome' || seenNodes.has(targetNode)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    seenNodes.add(targetNode);

    const nextEdges = edges.filter(edge => edge.source === targetNode.id);

    stack.push(...nextEdges);
  }
};

export const checkConfigPriorToStartEdge = (
  nodes,
  edges,
  startEdge,
  isEdgeAlreadyConfiguringInvestigation,
  isEdgeAlreadyConfiguringCase,
) => {
  let isInvestigationAlreadyConfigured = false;
  let canConfigureInvestigationOnEdge = true;
  let isCaseAlreadyConfigured = false;
  let canConfigureCaseOnEdge = false;
  let canConfigureAlertOrApplicationOnEdge = true;
  const seenNodes = new Set();
  const stack = [startEdge];

  while (stack.length > 0 && !(isInvestigationAlreadyConfigured && isCaseAlreadyConfigured)) {
    const currentEdge = stack.pop();

    if (currentEdge.data.config?.opensAlert || currentEdge.data.config?.opensApplication) {
      canConfigureAlertOrApplicationOnEdge = false;
    }

    if (currentEdge.data.config?.opensInvestigation) {
      isInvestigationAlreadyConfigured = true;
      canConfigureInvestigationOnEdge = isEdgeAlreadyConfiguringInvestigation || false;
    }

    if (currentEdge.data.config?.promotesToCase) {
      isCaseAlreadyConfigured = true;
      canConfigureCaseOnEdge = isEdgeAlreadyConfiguringCase || false;
    }

    if (isInvestigationAlreadyConfigured && isCaseAlreadyConfigured) {
      break;
    }

    const sourceNode = nodes.find(node => node.id === currentEdge.source);

    if (['source_workflow', 'start'].includes(sourceNode.data.type) || seenNodes.has(sourceNode)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    seenNodes.add(sourceNode);

    const nextEdges = edges.filter(edge => edge.target === sourceNode.id);

    if (nextEdges.length > 1) {
      const validationRes = nextEdges.reduce((acc, edge) => {
        const edgeCheck = checkConfigPriorToStartEdge(
          nodes,
          edges,
          edge,
          isEdgeAlreadyConfiguringInvestigation,
          isEdgeAlreadyConfiguringCase,
        );
        if (edgeCheck.isInvestigationAlreadyConfigured) {
          acc.investigationResults.push(edgeCheck);
        }
        if (edgeCheck.isCaseAlreadyConfigured) {
          acc.caseResults.push(edgeCheck);
        }
        return acc;
      }, { investigationResults: [], caseResults: [] });

      isInvestigationAlreadyConfigured = validationRes.investigationResults.length
        === nextEdges.length;
      canConfigureInvestigationOnEdge = isEdgeAlreadyConfiguringInvestigation
        || validationRes.investigationResults.length === 0;
      isCaseAlreadyConfigured = validationRes.caseResults.length > 0;
      if (!isCaseAlreadyConfigured && isInvestigationAlreadyConfigured) {
        canConfigureCaseOnEdge = true;
      }

      break;
    } else if (nextEdges.length === 1) {
      stack.push(nextEdges[0]);
    }
  }

  return {
    isInvestigationAlreadyConfigured,
    canConfigureInvestigationOnEdge,
    isCaseAlreadyConfigured,
    canConfigureCaseOnEdge,
    canConfigureAlertOrApplicationOnEdge,
  };
};

export const validateEdgeConfigurability = (
  nodes,
  edges,
  startEdge,
  isEdgeAlreadyConfiguringInvestigation = false,
  isEdgeAlreadyConfiguringCase = false,
) => {
  const validationObject = checkConfigPriorToStartEdge(
    nodes,
    edges,
    startEdge,
    isEdgeAlreadyConfiguringInvestigation,
    isEdgeAlreadyConfiguringCase,
  );

  const {
    isInvestigationAlreadyConfigured,
    isCaseAlreadyConfigured,
  } = validationObject;

  if (isInvestigationAlreadyConfigured && !isCaseAlreadyConfigured) {
    validationObject.canConfigureCaseOnEdge = true;
  }

  const nodesFollowingStartEdge = nodes.filter(node => node.id === startEdge.target);
  const nextNodeEdges = [];
  nodesFollowingStartEdge.forEach(targetNode => {
    if (targetNode.type === 'outcome') {
      return;
    }
    nextNodeEdges.push(...edges.filter(edge => edge.source === targetNode.id));
  });
  // If there is only one following node (outcome node), we can configure the alert or application on the edge
  if (!nextNodeEdges.length) {
    validationObject.canConfigureAlertOrApplicationOnEdge = true;
  }

  if (
    !validationObject.isInvestigationAlreadyConfigured
    || !validationObject.isCaseAlreadyConfigured
  ) {
    checkConfigAfterStartEdge(
      nodes,
      edges,
      startEdge,
      validationObject,
      isEdgeAlreadyConfiguringInvestigation,
    );
  }

  return validationObject;
};
